import { all, call, put, race, select, take } from 'redux-saga/effects';
import camelCase from 'lodash/camelCase';

import { GeneratorResult, OperationStatus, selectTyped, takeIf } from '@pressreader/appstore';
import { getContentRestrictions, getIssueContentRestrictions } from '@pressreader/content-api';
import { type IIssueInfo, IPublicationContentRestrictions, IssueContentRestrictions } from '@pressreader/content-types';
import { extractCidDateKeyString } from '@pressreader/content-utils';
import { selectShareConfig } from '@pressreader/global-config';
import { error } from '@pressreader/logger';
import { ShareProvider } from '@pressreader/social-types';
import { ExtractReturnType } from '@pressreader/types';

import {
    loadGlobalRestrictionsBegan,
    loadGlobalRestrictionsDone,
    loadGlobalRestrictionsFailed,
    loadIssueRestrictionsBegan,
    loadIssueRestrictionsDone,
    loadIssueRestrictionsFailed,
} from '../../actions/restrictions';
import { implementedShareProviders } from '../../constants';
import {
    selectIsSharingViaEmailAllowedByCidDateKey,
    selectIsSharingViaSocialNetworksAllowedByCidDateKey,
    selectIssueRestrictionsByCidDateKey,
    selectIssuesRestrictionsMap,
    selectIsTextViewAvailableByCidDateKey,
    selectPublicationsRestrictions,
} from '../../selectors';

import { loadIssueInfoByIssueKey } from './loaders';

function* getOrWaitForGlobalContentRestrictions(): GeneratorResult<{
    [cid: string]: IPublicationContentRestrictions;
} | null> {
    const publicationsRestrictions: ReturnType<typeof selectPublicationsRestrictions> = yield select(selectPublicationsRestrictions);
    if (publicationsRestrictions && publicationsRestrictions.status !== OperationStatus.None) {
        if (publicationsRestrictions.status === OperationStatus.Done) {
            return publicationsRestrictions.restrictions;
        }

        if (publicationsRestrictions.status === OperationStatus.Failed) {
            return null;
        }

        yield race({
            done: take(loadGlobalRestrictionsDone),
            failed: take(loadGlobalRestrictionsFailed),
        });

        return yield getOrWaitForGlobalContentRestrictions();
    }

    return null;
}

function* tryLoadPublicationsContentRestrictions(): GeneratorResult<{ [cid: string]: IPublicationContentRestrictions } | null> {
    try {
        let pubsRestrictions: ExtractReturnType<typeof getOrWaitForGlobalContentRestrictions> = yield getOrWaitForGlobalContentRestrictions();
        if (pubsRestrictions) {
            return pubsRestrictions;
        }

        pubsRestrictions = {};

        yield put(loadGlobalRestrictionsBegan());

        const result: ExtractReturnType<typeof getContentRestrictions> = yield call(getContentRestrictions);

        for (const itemDto of result) {
            for (const cid of itemDto.cids) {
                if (!pubsRestrictions[cid]) {
                    pubsRestrictions[cid] = {
                        anonymousContentRestricted: false,
                        blogging: false,
                        comments: false,
                        emailSharing: false,
                        readItLater: false,
                        smartFlow: false,
                        sound: false,
                        translation: false,
                    };
                }

                const restrictions = pubsRestrictions[cid];
                restrictions[camelCase(itemDto.ruleType) as keyof IPublicationContentRestrictions] = true;
            }
        }

        yield put(loadGlobalRestrictionsDone({ restrictions: pubsRestrictions }));

        return pubsRestrictions;
    } catch (e) {
        error(e);
        yield put(loadGlobalRestrictionsFailed({ error: e }));
    }

    return null;
}

function* getOrWaitForIssueContentRestrictions(cidDateKey: string): GeneratorResult<IssueContentRestrictions | null> {
    const issuesRestrictions: ReturnType<typeof selectIssuesRestrictionsMap> = yield select(selectIssuesRestrictionsMap);
    const issueRestriction = issuesRestrictions[cidDateKey];

    if (issueRestriction && issueRestriction.status !== OperationStatus.None) {
        if (issueRestriction.status === OperationStatus.Done) {
            return issueRestriction.restrictions;
        }

        if (issueRestriction.status === OperationStatus.Failed) {
            return null;
        }

        yield race({
            done: takeIf(loadIssueRestrictionsDone, a => a.payload.cidDateKey === cidDateKey),
            failed: takeIf(loadIssueRestrictionsFailed, a => a.payload.cidDateKey === cidDateKey),
        });

        return yield* getOrWaitForIssueContentRestrictions(cidDateKey);
    }

    return null;
}

function* tryLoadIssueContentRestrictionsByIssueKey(issueKey: string) {
    const cidDateKey = extractCidDateKeyString(issueKey);

    try {
        let restrictions = yield* getOrWaitForIssueContentRestrictions(cidDateKey);
        if (restrictions) {
            return restrictions;
        }

        yield put(loadIssueRestrictionsBegan({ cidDateKey }));

        const result: ExtractReturnType<typeof getIssueContentRestrictions> = yield call(getIssueContentRestrictions, issueKey);

        restrictions = Array.isArray(result) ? result[0].restrictions : result;

        yield put(loadIssueRestrictionsDone({ cidDateKey, restrictions }));

        return restrictions;
    } catch (e) {
        error(e);
        yield put(loadIssueRestrictionsFailed({ cidDateKey, error: e }));
    }

    return null;
}

function* loadIssueRestrictionsByIssueKey(issueKey: string): GeneratorResult<IssueContentRestrictions | null> {
    const cidDateKey = extractCidDateKeyString(issueKey);

    yield all([call(tryLoadPublicationsContentRestrictions), call(tryLoadIssueContentRestrictionsByIssueKey, issueKey)]);

    const result = yield* selectTyped(selectIssueRestrictionsByCidDateKey, { cidDateKey });

    return result;
}

function* isSmartFlowAvailableByIssueKey(issueKey: string): GeneratorResult<boolean> {
    yield loadIssueInfoByIssueKey(issueKey);
    yield loadIssueRestrictionsByIssueKey(issueKey);

    const result: ReturnType<typeof selectIsTextViewAvailableByCidDateKey> = yield select(selectIsTextViewAvailableByCidDateKey, {
        cidDateKey: extractCidDateKeyString(issueKey),
    });

    return result;
}

function getIssueSharingOptions(params: { issueKey: IIssueInfo['issueKey'] }): GeneratorResult<ShareProvider[]>;
function getIssueSharingOptions(params: { issueCidDateKey: IIssueInfo['issueCidDateKey'] }): GeneratorResult<ShareProvider[]>;
function* getIssueSharingOptions(params: {
    issueKey?: IIssueInfo['issueKey'];
    issueCidDateKey?: IIssueInfo['issueCidDateKey'];
}): GeneratorResult<ShareProvider[]> {
    const cidDateKey = params.issueCidDateKey || (params.issueKey ? extractCidDateKeyString(params.issueKey) : undefined);
    if (!cidDateKey) {
        return [];
    }

    const isSharingViaSocialNetworksAllowed = yield* selectTyped(selectIsSharingViaSocialNetworksAllowedByCidDateKey, {
        cidDateKey,
    });
    const isSharingViaEmailAllowed = yield* selectTyped(selectIsSharingViaEmailAllowedByCidDateKey, {
        cidDateKey,
    });

    // Fetch share config
    const shareConfig: ReturnType<typeof selectShareConfig> = yield select(selectShareConfig);

    type MenuItem = { provider: ShareProvider; enabled: boolean };
    const supported = (shareConfig.menu as MenuItem[])
        .filter(m => m.enabled && implementedShareProviders.indexOf(m.provider) >= 0)
        .map(m => m.provider)
        .filter(p => (p === 'shareeMail' ? isSharingViaEmailAllowed : isSharingViaSocialNetworksAllowed)); // 'copyUrl' is not allowed if social sharing is disabled

    return supported;
}

export { getIssueSharingOptions, isSmartFlowAvailableByIssueKey, loadIssueRestrictionsByIssueKey, tryLoadPublicationsContentRestrictions };
